// DJB-281: the "Show in iTunes" link of a song request without a library
// match. The Apple Music app treats https://music.apple.com/be/search?term=
// as a universal link but drops the search term, so the link must point at
// the track itself: the `trackViewUrl` of the iTunes Search API,
// https://music.apple.com/be/album/<slug>/<albumId>?i=<trackId>. In order:
//
// 1. `storeUrl` of the request when the server found the song (a track /
//    album URL as above; a search URL is not a hit and is ignored);
// 2. otherwise a lookup in the iTunes Search API on title + artist
//    (country=BE, entity=song), taking the best hit's trackViewUrl;
// 3. only when that finds nothing (or fails) the Apple Music search URL.
//
// storeUrl is often empty: the landing only sends a track id once an artist
// was picked from its suggestions (DJB-278), and older requests have none.

export const ITUNES_SEARCH_URL = 'https://music.apple.com/be/search?term=';
export const ITUNES_LOOKUP_URL = 'https://itunes.apple.com/search';
export const ITUNES_COUNTRY = 'BE';

const searchTerm = (title, artist) => `${title || ''} ${artist || ''}`.trim();

// Path 3: an Apple Music (BE) search on "title artist"
export const itunesSearchUrl = (title, artist) =>
  `${ITUNES_SEARCH_URL}${encodeURIComponent(searchTerm(title, artist))}`;

// Path 1: the storeUrl of the request, but only when it points at a track
// or album (anything on an Apple domain that is not a search page)
export const isTrackUrl = (url) => {
  if (typeof url !== 'string' || !url.trim()) return false;
  try {
    const parsed = new URL(url.trim());
    if (!/^https?:$/.test(parsed.protocol)) return false;
    if (!/(^|\.)(music|itunes|geo\.itunes)\.apple\.com$/.test(parsed.hostname)) return false;
    return !/^\/([a-z]{2}\/)?search\/?$/i.test(parsed.pathname);
  } catch (e) {
    return false;
  }
};

export const storeTrackUrl = (item) => (item && isTrackUrl(item.storeUrl) ? item.storeUrl.trim() : null);

// The URL of the search API request for path 2
export const lookupUrl = (title, artist) =>
  `${ITUNES_LOOKUP_URL}?term=${encodeURIComponent(searchTerm(title, artist))}` +
  `&country=${ITUNES_COUNTRY}&media=music&entity=song&limit=10`;

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Strip "(Remastered 2009)", "[Live]", "- Radio Edit" and the like
const bare = (s) => normalize(String(s || '').replace(/\s*[([].*?[)\]]/g, '').replace(/\s+-\s+.*$/, ''));

// Score a hit against the requested title + artist: exact title and artist
// win, then a bare-title match, then a contains match; ties keep the API's
// own order (its first result is its best guess)
export const hitScore = (hit, title, artist) => {
  const t = normalize(title);
  const a = normalize(artist);
  const ht = normalize(hit.trackName);
  const ha = normalize(hit.artistName);
  let score = 0;
  if (t) {
    if (ht === t) score += 4;
    else if (bare(hit.trackName) === bare(title)) score += 3;
    else if (ht.includes(t) || t.includes(ht)) score += 1;
  }
  if (a) {
    if (ha === a) score += 4;
    else if (ha.includes(a) || a.includes(ha)) score += 2;
  }
  return score;
};

export const bestHit = (results, title, artist) => {
  const songs = (Array.isArray(results) ? results : []).filter(
    (r) => r && typeof r.trackViewUrl === 'string' && r.trackViewUrl && (r.kind === 'song' || r.wrapperType === 'track')
  );
  if (songs.length === 0) return null;
  let best = songs[0];
  let bestScore = hitScore(best, title, artist);
  songs.slice(1).forEach((hit) => {
    const score = hitScore(hit, title, artist);
    if (score > bestScore) {
      best = hit;
      bestScore = score;
    }
  });
  return best;
};

// The Search API allows roughly 20 calls a minute and answers the rest
// without CORS headers, so: resolved lookups are kept per "title artist"
// (cards re-rendered or re-fetched after an action do not ask again),
// identical lookups in flight are shared, at most MAX_IN_FLIGHT run at a
// time, and a failed term is not retried for RETRY_AFTER_MS.
export const MAX_IN_FLIGHT = 2;
export const RETRY_AFTER_MS = 60 * 1000;

const cache = new Map();
const pending = new Map();
const failedAt = new Map();
const queue = [];
let running = 0;

export const clearLookupCache = () => {
  cache.clear();
  pending.clear();
  failedAt.clear();
  queue.length = 0;
  running = 0;
};

function pump() {
  while (running < MAX_IN_FLIGHT && queue.length > 0) {
    running += 1;
    queue.shift()().finally(taskDone);
  }
}

function taskDone() {
  running -= 1;
  pump();
}

const enqueue = (task) =>
  new Promise((resolve, reject) => {
    queue.push(() => task().then(resolve, reject));
    pump();
  });

const fetchTrackUrl = async (title, artist, fetchImpl) => {
  const response = await fetchImpl(lookupUrl(title, artist));
  if (!response || !response.ok) throw new Error(`iTunes lookup failed (${response && response.status})`);
  const body = await response.json();
  const hit = bestHit(body && body.results, title, artist);
  return hit ? hit.trackViewUrl : null;
};

// Path 2: the trackViewUrl of the best hit, or null when there is none.
// Rejects on a network / HTTP error (and, within RETRY_AFTER_MS of one, on
// the same term without asking again).
export const lookupTrackUrl = (title, artist, fetchImpl = typeof fetch === 'function' ? fetch : null, now = Date.now) => {
  const term = searchTerm(title, artist);
  if (!term || !fetchImpl) return Promise.resolve(null);
  const key = term.toLowerCase();
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (pending.has(key)) return pending.get(key);
  const failed = failedAt.get(key);
  if (failed !== undefined && now() - failed < RETRY_AFTER_MS) {
    return Promise.reject(new Error('iTunes lookup failed recently, not retried yet'));
  }
  const promise = enqueue(() => fetchTrackUrl(title, artist, fetchImpl)).then(
    (url) => {
      cache.set(key, url);
      failedAt.delete(key);
      pending.delete(key);
      return url;
    },
    (e) => {
      failedAt.set(key, now());
      pending.delete(key);
      throw e;
    }
  );
  pending.set(key, promise);
  return promise;
};

// The synchronous answer: storeUrl when usable, otherwise the search URL
// (the lookup of path 2 is asynchronous, see useItunesUrl in MatchPanel)
export const itunesUrl = (item) => storeTrackUrl(item) || itunesSearchUrl(item.titel, item.artiest);
